// The takeoff as a file. Tables come from quantity_takeoff; the bytes travel base64 so they
// survive MCP's JSON on the way to the page or to Claude Desktop.
import { isType, type PropsElement } from '../props';
import type { ToolFn, ToolSpec } from '../types';
import { round } from './select';
import { takeoff, type TakeoffResult } from './takeoff';
import { MATERIAL_CLASSES } from './vocabulary';

export const exportSpec: ToolSpec = {
  name: 'export_takeoff',
  description:
    'Write the quantity takeoff of the built elements (rooms left out) as a file the visitor downloads: xlsx with one sheet per grouping, or csv. groupBy picks one grouping (material, type or storey); leave it out for all three. Every table has the element count, volume in m³, area in m² and a totals row. Call this when the visitor asks for the quantities as Excel, a spreadsheet, csv or a file.',
  input_schema: {
    type: 'object',
    properties: {
      format: { type: 'string', enum: ['xlsx', 'csv'] },
      groupBy: { type: 'string', enum: ['material', 'type', 'storey'] },
    },
  },
};

export type GroupBy = 'material' | 'type' | 'storey';
export type Cell = string | number | null;

export interface TakeoffSheet {
  name: string;
  columns: string[];
  rows: Cell[][];
  totals: Cell[];
}

export interface Attachment {
  name: string;
  mime: string;
  size: number;
  base64: string;
}

const GROUPS: GroupBy[] = ['material', 'type', 'storey'];
const TITLES: Record<GroupBy, string> = { material: 'Material', type: 'Type', storey: 'Storey' };
const MIME = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
};

export const exportTakeoff =
  (elements: PropsElement[]): ToolFn =>
  async (input) => {
    const format = input.format === 'csv' ? 'csv' : 'xlsx';
    const one = GROUPS.find((g) => g === input.groupBy);
    const groups = one ? [one] : GROUPS;
    const run = takeoff(elements.filter((e) => !isType(e, 'IfcSpace')));
    const sheets = groups.map((g) => sheetOf(g, (q) => run(q) as TakeoffResult));
    const bytes = format === 'xlsx' ? await xlsxOf(sheets) : csvOf(sheets);
    const attachment: Attachment = {
      name: `takeoff${one ? '-' + one : ''}.${format}`,
      mime: MIME[format],
      size: bytes.length,
      base64: base64(bytes),
    };
    return { format, groupBy: one ?? 'all', sheets, attachment };
  };

function sheetOf(
  group: GroupBy,
  run: (input: Record<string, unknown>) => TakeoffResult,
): TakeoffSheet {
  const name = TITLES[group];
  const columns = [name, 'Elements', 'Volume (m³)', 'Area (m²)'];
  if (group === 'material') {
    const rows = Object.entries(MATERIAL_CLASSES).flatMap(([key, cls]) => {
      const volume = run({ quantity: 'volume', material: key });
      if (!volume.elements) return [];
      const area = run({ quantity: 'area', material: key });
      return [[cls.label.en, volume.elements, measured(volume), measured(area)]];
    });
    const sum = (i: number) => round(rows.reduce((s, r) => s + Number(r[i] ?? 0), 0));
    // one wall counts under masonry, insulation and gypsum alike, so elements do not add up here
    return { name, columns, rows, totals: ['Total', null, sum(2), sum(3)] };
  }
  const field = group === 'type' ? 'by_type' : 'by_storey';
  const count = run({ quantity: 'count' });
  const volume = run({ quantity: 'volume' });
  const area = run({ quantity: 'area' });
  const rows: Cell[][] = Object.keys(count[field]).map((k) => [
    k,
    count[field][k]!,
    volume[field][k] ?? null,
    area[field][k] ?? null,
  ]);
  if (group === 'type') rows.sort((a, b) => Number(b[2] ?? -1) - Number(a[2] ?? -1));
  return {
    name,
    columns,
    rows,
    totals: ['Total', count.elements, measured(volume), measured(area)],
  };
}

// empty rather than 0 when nothing in the group carried the quantity
const measured = (r: TakeoffResult) => (r.elements > r.missing ? r.total : null);

async function xlsxOf(sheets: TakeoffSheet[]): Promise<Uint8Array> {
  const { utils, write } = await import('xlsx');
  const book = utils.book_new();
  for (const s of sheets) {
    const sheet = utils.aoa_to_sheet([s.columns, ...s.rows, s.totals]);
    sheet['!cols'] = s.columns.map((_, i) => ({ wch: i === 0 ? 28 : 14 }));
    utils.book_append_sheet(book, sheet, s.name);
  }
  return new Uint8Array(write(book, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer);
}

function csvOf(sheets: TakeoffSheet[]): Uint8Array {
  const lines = [['Grouping', 'Name', ...sheets[0]!.columns.slice(1)]];
  for (const s of sheets)
    for (const row of [...s.rows, s.totals]) lines.push([s.name, ...row.map(cell)]);
  // the byte order mark tells Excel the file is UTF-8, or m³ comes out garbled
  const text = '\ufeff' + lines.map((l) => l.map(quote).join(',')).join('\r\n') + '\r\n';
  return new TextEncoder().encode(text);
}

const cell = (c: Cell) => (c == null ? '' : String(c));
const quote = (s: string) => (/[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}
